import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LockKeyhole } from 'lucide-react';
import { toast } from 'sonner';
import { HostPrimaryButton } from '../components/HostSettingsCard';
import { HostOnboardingShell } from '../components/host-onboarding/HostOnboardingShell';
import { HostListingPreviewCard } from '../components/host-onboarding/HostListingPreviewCard';
import { useBackTarget } from '../hooks/useBackTarget';
import { getPendingHostListingInviteData, getPendingHostListingInviteId, clearPendingHostListingInviteId } from '../utils/deepLinkHandler';
import {
  clearHostOnboardingResult,
  getCachedListingPreview,
  isValidAirbnbListingUrl,
  isValidIcalUrl,
  normalizeHostUrl,
  saveHostListingDraft,
  setCachedListingPreview,
} from '../utils/hostOnboarding';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inputTextStyle = {
  width: '100%',
  padding: '12px 14px',
  borderRadius: 16,
  border: '1px solid var(--card-border)',
  background: 'var(--surface)',
  fontFamily: "'Inria Serif', serif",
  fontSize: 18,
  color: 'var(--text)',
  textAlign: 'center',
  outline: 'none',
  boxShadow: 'var(--premium-shadow-nested)',
};

const cardHeadingStyle = {
  fontFamily: "'Inria Serif', serif",
  fontSize: 22,
  fontWeight: 700,
  color: 'var(--text)',
  margin: 0,
  lineHeight: 1.08,
  textAlign: 'center',
};

const fieldCardStyle = {
  width: '100%',
  padding: '20px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

export default function HostVerifyListingPage() {
  const navigate = useNavigate();
  // If the host arrived from "My listings" (returning host adding another
  // listing), the back arrow should return them there — not to the first-time
  // onboarding intro. HostListingsPage stashes the origin in sessionStorage.
  const returnTo = (() => {
    try {
      return sessionStorage.getItem('host_verify_listing_return_to') || '';
    } catch {
      return '';
    }
  })();
  const goBack = useBackTarget(
    returnTo
      ? () => {
          try { sessionStorage.removeItem('host_verify_listing_return_to'); } catch { /* noop */ }
          navigate(returnTo);
        }
      : undefined,
  );
  const [listingUrl, setListingUrl] = useState('');
  const [icalUrl, setIcalUrl] = useState('');
  const [touched, setTouched] = useState({ listing: false, ical: false });
  const [preview, setPreview] = useState(null);
  const [previewReadyUrl, setPreviewReadyUrl] = useState('');
  const [prefilledInvite, setPrefilledInvite] = useState(null);
  const [loading, setLoading] = useState(false);
  const previewRequestRef = useRef(0);

  const listingValid = useMemo(() => isValidAirbnbListingUrl(listingUrl), [listingUrl]);
  const icalValid = useMemo(() => isValidIcalUrl(icalUrl), [icalUrl]);
  const listingError = touched.listing && !listingValid ? 'Please enter a valid Airbnb listing URL' : '';
  const icalError = touched.ical && !icalValid ? 'Please enter a valid calendar (iCal) link' : '';
  const buttonDisabled = !listingValid || !icalValid || loading;

  useEffect(() => {
    const loadPendingInvite = async () => {
      const pendingInviteData = getPendingHostListingInviteData();
      if (pendingInviteData?.listing_url) {
        const normalizedListingUrl = normalizeHostUrl(pendingInviteData.listing_url);
        setListingUrl(normalizedListingUrl);
        setPrefilledInvite(pendingInviteData);
        setPreviewReadyUrl(normalizedListingUrl);
        setPreview({
          title: pendingInviteData.listing_title || '',
          image: pendingInviteData.listing_image || '',
          location_label: pendingInviteData.listing_location_label || '',
        });
        return;
      }

      const pendingInviteId = getPendingHostListingInviteId();
      if (!pendingInviteId) return;
      try {
        const response = await axios.get(`${API}/host/listing-invites/${pendingInviteId}/landing`);
        const inviteData = response.data;
        const normalizedListingUrl = normalizeHostUrl(inviteData.listing_url);
        setListingUrl(normalizedListingUrl);
        setPrefilledInvite(inviteData);
        setPreviewReadyUrl(normalizedListingUrl);
        if (inviteData.listing_title || inviteData.listing_image || inviteData.listing_location_label) {
          setPreview({
            title: inviteData.listing_title || '',
            image: inviteData.listing_image || '',
            location_label: inviteData.listing_location_label || '',
          });
        }
      } catch {
        clearPendingHostListingInviteId();
      }
    };

    void loadPendingInvite();
  }, []);

  const fetchPreview = useCallback(async (rawUrl) => {
    const nextUrl = normalizeHostUrl(rawUrl);
    if (!isValidAirbnbListingUrl(nextUrl)) {
      setPreview(null);
      setPreviewReadyUrl('');
      return;
    }

    const cachedPreview = getCachedListingPreview(nextUrl);
    if (cachedPreview) {
      setPreview(cachedPreview);
      setPreviewReadyUrl(nextUrl);
      return;
    }

    const requestId = previewRequestRef.current + 1;
    previewRequestRef.current = requestId;
    try {
      const response = await axios.post(`${API}/host/listing-preview`, { listing_url: nextUrl });
      if (previewRequestRef.current !== requestId) {
        return;
      }
      const nextPreview = response.data?.preview || null;
      if (nextPreview) {
        setCachedListingPreview(nextUrl, nextPreview);
        setPreview(nextPreview);
        setPreviewReadyUrl(nextUrl);
      } else {
        setPreview(null);
        setPreviewReadyUrl(nextUrl);
      }
    } catch (error) {
      if (previewRequestRef.current === requestId) {
        setPreview(null);
        setPreviewReadyUrl(nextUrl);
      }
    }
  }, []);

  const handleNext = async () => {
    const normalizedListingUrl = normalizeHostUrl(listingUrl);
    const normalizedIcalUrl = normalizeHostUrl(icalUrl);

    setTouched({ listing: true, ical: true });
    if (!isValidAirbnbListingUrl(normalizedListingUrl)) {
      toast.error('Please enter a valid Airbnb listing URL');
      return;
    }
    if (!isValidIcalUrl(normalizedIcalUrl)) {
      toast.error('Please enter a valid calendar (iCal) link');
      return;
    }

    setLoading(true);
    try {
      clearHostOnboardingResult();
      // Stash the validated draft so the agreement page can submit
      // /host/apply only AFTER the host ticks "I agree". We deliberately
      // do NOT call /host/apply yet — the agreement and the listing
      // creation must happen as a single user-approved step.
      saveHostListingDraft({
        listing_url: normalizedListingUrl,
        ical_url: normalizedIcalUrl,
        preview: preview || null,
        prefilled_invite_id: prefilledInvite?.invite_id || '',
      });
      navigate('/host-agreement');
    } catch (err) {
      toast.error('Could not continue. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleListingPaste = (event) => {
    const pastedUrl = event.clipboardData?.getData('text') || '';
    window.requestAnimationFrame(() => {
      void fetchPreview(pastedUrl || event.target.value || listingUrl);
    });
  };

  return (
    <HostOnboardingShell
      onBack={goBack}
      pageTestId="host-verify-listing-page"
      backTestId="host-verify-listing-back-button"
      logoTestId="host-verify-listing-logo"
      scrollable
      footer={
        <HostPrimaryButton onClick={handleNext} dataTestId="host-verify-listing-connect-button" disabled={buttonDisabled}>
          {loading ? 'Loading…' : 'Review agreement'}
        </HostPrimaryButton>
      }
    >
      <h1 data-testid="host-verify-listing-title" style={{ fontFamily: "'Inria Serif', serif", fontSize: 22, fontWeight: 700, color: 'var(--text)', textAlign: 'center', margin: '22px 0 0', lineHeight: 1.1 }}>
        Add your listing
      </h1>
      <p data-testid="host-verify-listing-subtitle" style={{ fontFamily: "'Inria Serif', serif", fontSize: 16, fontWeight: 700, color: 'var(--text)', textAlign: 'center', margin: '12px 0 0', lineHeight: 1.15 }}>
        Connect your listing in seconds.
      </p>

      {prefilledInvite ? (
        <div className="premium-card" data-testid="host-prefilled-invite-card" style={{ ...fieldCardStyle, marginTop: 22, textAlign: 'center' }}>
          <p data-testid="host-prefilled-invite-label" style={cardHeadingStyle}>
            Listing added from your invite
          </p>
          <p data-testid="host-prefilled-invite-copy" style={{ fontFamily: "'Inria Serif', serif", fontSize: 15, color: 'var(--text-2)', margin: 0, lineHeight: 1.3 }}>
            Your Airbnb listing is already attached. Only paste your calendar iCal to continue.
          </p>
        </div>
      ) : (
        <div className="premium-card" data-testid="host-listing-url-card" style={{ ...fieldCardStyle, marginTop: 22 }}>
          <p data-testid="host-listing-url-label" style={cardHeadingStyle}>
            Paste your Airbnb listing URL
          </p>

          <input
            type="text"
            value={listingUrl}
            onChange={(event) => {
              const nextValue = event.target.value;
              setListingUrl(nextValue);
              if (previewReadyUrl && normalizeHostUrl(nextValue) !== previewReadyUrl) {
                setPreview(null);
              }
            }}
            onBlur={() => {
              setTouched((current) => ({ ...current, listing: true }));
              void fetchPreview(listingUrl);
            }}
            onPaste={handleListingPaste}
            placeholder="ex: airbnb.com/rooms/321..."
            data-testid="host-verify-listing-input"
            autoComplete="off"
            spellCheck={false}
            style={inputTextStyle}
          />

          {listingError ? (
            <p data-testid="host-listing-url-error" style={{ fontFamily: "'Inria Serif', serif", fontSize: 14, color: '#C83A4E', margin: '-2px 0 0', textAlign: 'center' }}>
              {listingError}
            </p>
          ) : null}

          <button
            onClick={() => navigate('/host-verify-details?topic=listing')}
            data-testid="host-listing-url-help-button"
            style={{ fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 600, color: 'var(--accent-purple)', background: 'none', border: 'none', textDecoration: 'underline', textUnderlineOffset: '4px', cursor: 'pointer', padding: 0 }}
          >
            Where do I find this?
          </button>
        </div>
      )}

      <HostListingPreviewCard preview={preview} />

      <div className="premium-card" data-testid="host-ical-url-card" style={{ ...fieldCardStyle, marginTop: 16 }}>
        <p data-testid="host-ical-url-label" style={cardHeadingStyle}>
          Paste your calendar (iCal) URL
        </p>

        <input
          type="text"
          value={icalUrl}
          onChange={(event) => setIcalUrl(event.target.value)}
          onBlur={() => setTouched((current) => ({ ...current, ical: true }))}
          placeholder="ex: airbnb.com/calendar/ical/123456.ics"
          data-testid="host-verify-ical-input"
          autoComplete="off"
          spellCheck={false}
          style={inputTextStyle}
        />

        {icalError ? (
          <p data-testid="host-ical-url-error" style={{ fontFamily: "'Inria Serif', serif", fontSize: 14, color: '#C83A4E', margin: '-2px 0 0', textAlign: 'center' }}>
            {icalError}
          </p>
        ) : null}

        <button
          onClick={() => navigate('/host-verify-details?topic=calendar')}
          data-testid="host-ical-url-help-button"
          style={{ fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 600, color: 'var(--accent-purple)', background: 'none', border: 'none', textDecoration: 'underline', textUnderlineOffset: '4px', cursor: 'pointer', padding: 0 }}
        >
          Where do I find this?
        </button>
      </div>

      <div className="premium-card" data-testid="host-sync-privacy-card" style={{ width: '100%', marginTop: 20, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14, boxShadow: 'none', border: '1px solid var(--card-border)' }}>
        <div data-testid="host-sync-privacy-icon-shell" style={{ width: 42, height: 42, borderRadius: 9999, border: '1px solid var(--card-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <LockKeyhole size={20} color="var(--text)" strokeWidth={1.7} />
        </div>
        <p data-testid="host-sync-privacy-text" style={{ fontFamily: "'Inria Serif', serif", fontSize: 15, fontWeight: 700, color: 'var(--text)', lineHeight: 1.2, margin: 0, textAlign: 'left' }}>
          We only use this to sync your listing and availability.
        </p>
      </div>
    </HostOnboardingShell>
  );
}
